import {type Action, createReducer, on} from '@ngrx/store'
import moment from 'moment'
import {ScheduleMode, type ScheduleSettings} from '@models/schedule'
import {ALL_DAYS} from '@models/schedule.model'
import * as ScheduleActions from './schedule.actions'
import type {Appointment} from '@models/appointment'
import type {Outlet} from '@models/outlet'

export type ScheduleState = {
  appointmentsLoaded: number
  settings: ScheduleSettings
  appointments: Appointment[]
  appointmentDeleted: Appointment
  outletsPlannedFiltered: Outlet[]
  isMapView: boolean
  appointmentChangeTracker: number
}

const initialState: ScheduleState = {
  appointmentsLoaded: 0,
  settings: {
    mode: ScheduleMode.WEEK,
    day: moment(),
    collapsedCalendar: true,
    showLateOutlets: false,
    settings: [
      {
        id: ALL_DAYS,
        label: 'show_business_days',
        value: false,
        visibleInMode: [ScheduleMode.WEEK],
      },
    ],
  },
  appointments: [],
  appointmentDeleted: null,
  outletsPlannedFiltered: [],
  isMapView: false,
  appointmentChangeTracker: 0,
}

export const scheduleReducer = (state: ScheduleState | undefined, action: Action) => reducer(state, action)

const reducer = createReducer(
  initialState,
  on(ScheduleActions.setSettingsRequest, (state, {settings}) => ({
    ...state,
    settings,
  })),
  on(ScheduleActions.setSettingCalendarMode, (state, {mode}) => ({
    ...state,
    settings: {
      ...state.settings,
      mode,
    },
  })),
  on(ScheduleActions.setNotVisitedOutlets, (state, {notVisitedOutlets}) => ({
    ...state,
    notVisitedOutlets,
  })),
  on(ScheduleActions.getAppointmentsResponse, (state, {appointments}) =>
    appointments.length ?
      {
        ...state,
        appointmentsLoaded: state.appointmentsLoaded + 1,
        appointments: state.appointments
          .filter(a => !appointments.some(a2 => a2.appointmentId === a.appointmentId))
          .concat(appointments)
          .sort((a, b) => a.appointmentDate - b.appointmentDate),
        appointmentChangeTracker: state.appointmentChangeTracker + 1,
      }
    : {
        ...state,
        appointmentsLoaded: state.appointmentsLoaded + 1,
        appointmentChangeTracker: state.appointmentChangeTracker + 1,
      }
  ),

  on(ScheduleActions.removeScheduleAppointmentResponse, (state, {appointment}) => ({
    ...state,
    appointments: state.appointments.filter(a => a.appointmentId !== appointment.appointmentId),
    appointmentDeleted: appointment,
    appointmentChangeTracker: state.appointmentChangeTracker + 1,
  })),
  on(ScheduleActions.setScheduleOutlet, (state, {outlet}) => ({
    ...state,
    appointments: state.appointments.map(appointment =>
      appointment.targetId?.toString() === outlet.id?.toString() ? {...appointment, outletInfo: outlet} : appointment
    ),
  })),
  on(ScheduleActions.resetSchedule, state => initialState),
  on(ScheduleActions.updateScheduleAppointmentResponse, (state, {appointment}) => ({
    ...state,
    appointments: state.appointments.map(a => (a.appointmentId === appointment.appointmentId ? appointment : a)),
  })),
  on(ScheduleActions.setIsMapViewSchedule, (state, {value}) => ({
    ...state,
    isMapView: value,
  })),
  on(ScheduleActions.setAppointmentChangeTracker, (state, {value}) => ({
    ...state,
    appointmentChangeTracker: value,
  }))
)
